import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Move, SplitSquareHorizontal, Merge, Plus, Trash2 } from "lucide-react";

const TABS = [
  { id: "move", label: "Move", icon: Move },
  { id: "split", label: "Split", icon: SplitSquareHorizontal },
  { id: "merge", label: "Merge", icon: Merge },
];

export default function ContainerOps() {
  const [tab, setTab] = useState("move");
  return (
    <div className="space-y-6 animate-fade-in" data-testid="container-ops">
      <div>
        <div className="data-label">Within a godown</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold">Move &amp; Repack</h1>
      </div>
      <div className="flex gap-1 border-b border-border">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`ops-tab-${t.id}`}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold uppercase tracking-widest border-b-2 -mb-px ${
              tab === t.id ? "border-primary text-primary" : "border-transparent text-muted-foreground"
            }`}
          ><t.icon className="w-4 h-4" /> {t.label}</button>
        ))}
      </div>
      {tab === "move" && <MoveTab />}
      {tab === "split" && <SplitTab />}
      {tab === "merge" && <MergeTab />}
    </div>
  );
}

function useContainers() {
  return useQuery({
    queryKey: ["containers-list"],
    queryFn: async () => (await api.get("/containers")).data,
  });
}
function useLocations() {
  return useQuery({
    queryKey: ["locations"],
    queryFn: async () => (await api.get("/locations")).data.filter(l => l.active),
  });
}

function MoveTab() {
  const { data: containers = [] } = useContainers();
  const { data: locations = [] } = useLocations();
  const qc = useQueryClient();
  const [selected, setSelected] = useState({});
  const [toLoc, setToLoc] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("");

  const filtered = containers.filter(c =>
    !filter || c.code.toLowerCase().includes(filter.toLowerCase())
    || c.description.toLowerCase().includes(filter.toLowerCase())
  );
  const ids = Object.keys(selected).filter(k => selected[k]);

  const submit = async () => {
    if (!ids.length || !toLoc) return;
    setBusy(true);
    try {
      await api.post("/containers/move", { container_ids: ids, to_location_id: toLoc, note });
      toast.success(`Moved ${ids.length} container${ids.length > 1 ? "s" : ""}`);
      setSelected({}); setNote("");
      qc.invalidateQueries();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-border rounded-sm p-4 space-y-4" data-testid="move-tab">
      <input className="field-sm" placeholder="Filter containers…"
        value={filter} onChange={(e) => setFilter(e.target.value)}
        data-testid="move-filter" />
      <div className="max-h-96 overflow-auto border border-border rounded-sm">
        <ul className="divide-y divide-border">
          {filtered.map(c => (
            <li key={c.id} className="flex items-center gap-3 p-3">
              <input
                type="checkbox"
                data-testid={`move-select-${c.id}`}
                checked={!!selected[c.id]}
                onChange={(e) => setSelected(s => ({ ...s, [c.id]: e.target.checked }))}
              />
              <div className="flex-1 min-w-0">
                <div className="container-code">{c.code}</div>
                <div className="text-xs text-muted-foreground truncate">{c.description}</div>
              </div>
              <div className="font-mono text-sm">{c.current_qty}</div>
            </li>
          ))}
          {!filtered.length && <li className="p-4 text-sm text-muted-foreground">No containers.</li>}
        </ul>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <select className="field" value={toLoc} onChange={(e) => setToLoc(e.target.value)}
                data-testid="move-to-location">
          <option value="">Destination location</option>
          {locations.map(l => <option key={l.id} value={l.id}>{l.code} · {l.name}</option>)}
        </select>
        <input className="field" placeholder="Note (optional)"
               value={note} onChange={(e) => setNote(e.target.value)}
               data-testid="move-note" />
      </div>
      <div className="flex justify-between items-center">
        <div className="text-sm text-muted-foreground">{ids.length} selected</div>
        <button onClick={submit} disabled={busy || !ids.length || !toLoc}
                className="btn-primary" data-testid="move-submit">
          {busy ? "Moving…" : "Move containers"}
        </button>
      </div>
    </div>
  );
}

function SplitTab() {
  const { data: containers = [] } = useContainers();
  const { data: locations = [] } = useLocations();
  const qc = useQueryClient();
  const [src, setSrc] = useState("");
  const [targets, setTargets] = useState([{ qty: "", description: "", packaging_item_id: "", packaging_source_location_id: "" }]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [pkgItems, setPkgItems] = useState([]);

  useEffect(() => {
    api.get("/items").then(r => setPkgItems(r.data.filter(i => i.category === "packaging"))).catch(() => {});
  }, []);

  const srcC = containers.find(c => c.id === src);
  const total = targets.reduce((s, t) => s + (Number(t.qty) || 0), 0);
  const ok = srcC && total > 0 && total <= srcC.current_qty && targets.every(t => t.qty && t.description);

  const updT = (i, patch) => setTargets(T => T.map((t, ix) => ix === i ? { ...t, ...patch } : t));
  const addT = () => setTargets(T => [...T, { qty: "", description: "", packaging_item_id: "", packaging_source_location_id: "" }]);
  const rmT = (i) => setTargets(T => T.filter((_, ix) => ix !== i));

  const submit = async () => {
    if (!ok) return;
    setBusy(true);
    try {
      await api.post("/containers/split", {
        source_container_id: src,
        note: note || null,
        targets: targets.map(t => ({
          qty: Number(t.qty), description: t.description,
          packaging_item_id: t.packaging_item_id || null,
          packaging_source_location_id: t.packaging_source_location_id || null,
        })),
      });
      toast.success("Split posted");
      setSrc(""); setTargets([{ qty: "", description: "", packaging_item_id: "", packaging_source_location_id: "" }]); setNote("");
      qc.invalidateQueries();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-border rounded-sm p-4 space-y-4" data-testid="split-tab">
      <div>
        <span className="data-label">Source container</span>
        <select className="field mt-1" value={src} onChange={(e) => setSrc(e.target.value)}
                data-testid="split-source">
          <option value="">Choose source…</option>
          {containers.map(c => (
            <option key={c.id} value={c.id}>{c.code} — {c.current_qty} — {c.description}</option>
          ))}
        </select>
      </div>
      {srcC && (
        <>
          <div className="text-xs text-muted-foreground">
            Available: <span className="font-mono font-semibold">{srcC.current_qty}</span>
          </div>
          <div className="space-y-2">
            {targets.map((t, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-start p-2 border border-border rounded-sm" data-testid={`split-target-${i}`}>
                <input type="number" step="0.001" placeholder="Qty"
                       className="field-sm col-span-3 font-mono"
                       value={t.qty} onChange={(e) => updT(i, { qty: e.target.value })}
                       data-testid={`split-target-${i}-qty`} />
                <input placeholder="Container description"
                       className="field-sm col-span-4"
                       value={t.description} onChange={(e) => updT(i, { description: e.target.value })}
                       data-testid={`split-target-${i}-desc`} />
                <select className="field-sm col-span-3"
                        value={t.packaging_item_id}
                        onChange={(e) => updT(i, { packaging_item_id: e.target.value })}
                        data-testid={`split-target-${i}-pkg`}>
                  <option value="">No packaging</option>
                  {pkgItems.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <select className="field-sm col-span-2"
                        disabled={!t.packaging_item_id}
                        value={t.packaging_source_location_id}
                        onChange={(e) => updT(i, { packaging_source_location_id: e.target.value })}
                        data-testid={`split-target-${i}-pkgloc`}>
                  <option value="">Src loc</option>
                  {locations.map(l => <option key={l.id} value={l.id}>{l.code}</option>)}
                </select>
                <button className="text-muted-foreground hover:text-destructive col-span-12 self-end"
                        onClick={() => rmT(i)}
                        disabled={targets.length === 1}
                        data-testid={`split-target-${i}-rm`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button onClick={addT} className="text-primary text-xs font-semibold uppercase tracking-widest flex items-center gap-1"
                    data-testid="split-add-target">
              <Plus className="w-4 h-4" /> Add target container
            </button>
          </div>
          <input className="field" placeholder="Note (optional)"
                 value={note} onChange={(e) => setNote(e.target.value)}
                 data-testid="split-note" />
          <div className="flex justify-between items-center">
            <div className="text-sm">
              Splitting <span className="font-mono font-semibold">{total.toFixed(3)}</span>
              {" / "}<span className="font-mono">{srcC.current_qty}</span>
            </div>
            <button onClick={submit} disabled={!ok || busy} className="btn-primary" data-testid="split-submit">
              {busy ? "Posting…" : "Post split"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function MergeTab() {
  const { data: containers = [] } = useContainers();
  const qc = useQueryClient();
  const [srcId, setSrcId] = useState("");
  const [dstId, setDstId] = useState("");
  const [qty, setQty] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const src = containers.find(c => c.id === srcId);
  const dst = containers.find(c => c.id === dstId);
  const sameItem = src && dst && src.item_id === dst.item_id;

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/containers/merge", {
        source_container_id: srcId,
        dest_container_id: dstId,
        qty: qty ? Number(qty) : null,
        note: note || null,
      });
      toast.success("Merge posted");
      setSrcId(""); setDstId(""); setQty(""); setNote("");
      qc.invalidateQueries();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-border rounded-sm p-4 space-y-4" data-testid="merge-tab">
      <p className="text-sm text-muted-foreground">
        Source and destination must be the same item. Cross-batch merges are allowed —
        the destination container tracks the batch mix for full traceability.
      </p>
      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <span className="data-label">Source (pour from)</span>
          <select className="field mt-1" value={srcId} onChange={(e) => setSrcId(e.target.value)}
                  data-testid="merge-source">
            <option value="">…</option>
            {containers.map(c => (
              <option key={c.id} value={c.id}>{c.code} — {c.current_qty}</option>
            ))}
          </select>
        </div>
        <div>
          <span className="data-label">Destination (pour into)</span>
          <select className="field mt-1" value={dstId} onChange={(e) => setDstId(e.target.value)}
                  data-testid="merge-dest">
            <option value="">…</option>
            {containers.filter(c => c.id !== srcId).map(c => (
              <option key={c.id} value={c.id}>{c.code} — {c.current_qty}</option>
            ))}
          </select>
        </div>
      </div>
      {srcId && dstId && src && dst && src.item_id !== dst.item_id && (
        <div className="text-sm text-destructive">Different items — merge is blocked.</div>
      )}
      {srcId && dstId && src && dst && src.batch_id !== dst.batch_id && src.item_id === dst.item_id && (
        <div className="text-sm text-secondary bg-secondary/10 border border-secondary/30 rounded-sm p-2">
          Cross-batch merge — destination will track mix composition.
        </div>
      )}
      <div>
        <span className="data-label">Qty to pour (blank = all of source)</span>
        <input type="number" step="0.001" className="field mt-1 font-mono"
               value={qty} onChange={(e) => setQty(e.target.value)}
               data-testid="merge-qty" />
      </div>
      <input className="field" placeholder="Note (optional)" value={note}
             onChange={(e) => setNote(e.target.value)} data-testid="merge-note" />
      <div className="flex justify-end">
        <button onClick={submit} className="btn-primary"
                disabled={busy || !srcId || !dstId || !sameItem}
                data-testid="merge-submit">
          {busy ? "Merging…" : "Post merge"}
        </button>
      </div>
    </div>
  );
}
